// src/components/Chatbot.js
import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../utils/LanguageContext';
import '../styles/chatbot.css';
import api from '../services/api';

const Chatbot = () => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Quick action buttons
  const quickActions = {
    en: [
      { id: 1, text: "🌳 Recommend a tree", action: "recommend" },
      { id: 2, text: "📍 Track my trees", action: "track" },
      { id: 3, text: "💰 Pricing info", action: "pricing" },
      { id: 4, text: "🌱 Planting tips", action: "tips" },
    ],
    ru: [
      { id: 1, text: "🌳 Порекомендуйте дерево", action: "recommend" },
      { id: 2, text: "📍 Отследить мои деревья", action: "track" },
      { id: 3, text: "💰 Информация о ценах", action: "pricing" },
      { id: 4, text: "🌱 Советы по посадке", action: "tips" },
    ],
    uz: [
      { id: 1, text: "🌳 Daraxt tavsiya qiling", action: "recommend" },
      { id: 2, text: "📍 Daraxtlarimni kuzatish", action: "track" },
      { id: 3, text: "💰 Narxlar haqida", action: "pricing" },
      { id: 4, text: "🌱 Ekish maslahatlari", action: "tips" },
    ]
  };

  // Bot responses
  const botResponses = {
    en: {
      welcome: "Hi! 👋 I'm EcoBot, your tree planting assistant. How can I help you today?",
      recommend: "Great choice! 🌳 For Uzbekistan's climate, I recommend:\n\n• White Mulberry - drought resistant\n• Eldar Pine - fast growing\n• Sweet Cherry - produces fruit\n\nWould you like to see these in our shop?",
      track: "You can track all your planted trees on our interactive Map page! 🗺️ Each tree has GPS coordinates and regular photo updates.",
      pricing: "Our pricing is transparent:\n\n💰 Trees: 40,000-120,000 UZS\n\n✅ Free professional planting\n✅ 30% discount on all orders\n✅ Lifetime tracking",
      tips: "🌱 Essential planting tips:\n\n1. Plant in spring or fall\n2. Choose sunny locations\n3. Water regularly for first month\n4. Mulch around the base\n5. Monitor for pests\n\nOur team handles all of this for you!",
      default: "I'm here to help! Try asking about tree recommendations, pricing, or planting tips. You can also use the quick actions below! 🌿"
    },
    ru: {
      welcome: "Привет! 👋 Я EcoBot, ваш помощник по посадке деревьев. Чем могу помочь?",
      recommend: "Отличный выбор! 🌳 Для климата Узбекистана рекомендую:\n\n• Белая шелковица - засухоустойчива\n• Сосна Эльдара - быстрорастущая\n• Черешня - плодоносит\n\nХотите увидеть их в магазине?",
      track: "Вы можете отслеживать все посаженные деревья на интерактивной карте! 🗺️ У каждого дерева есть GPS-координаты и регулярные фотоотчеты.",
      pricing: "Наши цены прозрачны:\n\n💰 Деревья: 40,000-120,000 UZS\n\n✅ Бесплатная посадка\n✅ Скидка 30%\n✅ Пожизненное отслеживание",
      tips: "🌱 Основные советы:\n\n1. Сажайте весной или осенью\n2. Выбирайте солнечные места\n3. Поливайте регулярно первый месяц\n4. Мульчируйте вокруг основания\n5. Следите за вредителями\n\nНаша команда справится со всем!",
      default: "Я здесь, чтобы помочь! Спросите о рекомендациях деревьев, ценах или советах. Или используйте быстрые действия ниже! 🌿"
    },
    uz: {
      welcome: "Salom! 👋 Men EcoBot, daraxt ekish bo'yicha yordamchiman. Sizga qanday yordam bera olaman?",
      recommend: "Ajoyib tanlov! 🌳 O'zbekiston iqlimi uchun tavsiya qilaman:\n\n• Oq tut - qurg'oqchilikka chidamli\n• Eldar qarag'ayi - tez o'sadigan\n• Shirin olcha - meva beradi\n\nBularni do'konimizda ko'rishni xohlaysizmi?",
      track: "Barcha ekilgan daraxtlaringizni interaktiv xaritada kuzatishingiz mumkin! 🗺️ Har bir daraxtning GPS koordinatalari va muntazam foto hisobotlari bor.",
      pricing: "Bizning narxlar shaffof:\n\n💰 Daraxtlar: 40,000-120,000 UZS\n\n✅ Bepul professional ekish\n✅ 30% chegirma\n✅ Umrboqiy kuzatuv",
      tips: "🌱 Muhim maslahatlar:\n\n1. Bahor yoki kuzda eking\n2. Quyoshli joylarni tanlang\n3. Birinchi oy muntazam sug'oring\n4. Asosini mulchlang\n5. Zararkunandalarni kuzating\n\nBizning jamoa hammasi bilan shug'ullanadi!",
      default: "Men yordam berish uchun shu yerdaman! Daraxt tavsifalari, narxlar yoki maslahatlar haqida so'rang. Yoki quyidagi tezkor harakatlardan foydalaning! 🌿"
    }
  };

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: Date.now(),
          text: botResponses[language].welcome,
          sender: 'bot',
          timestamp: new Date()
        }
      ]);
    }
  }, []);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle bot response
  const getBotResponse = (userMessage, action = null) => {
    const responses = botResponses[language];

    if (action) {
      return responses[action] || responses.default;
    }

    const msg = userMessage.toLowerCase();

    // Keyword matching
    if (msg.includes('tree') || msg.includes('daraxt') || msg.includes('дерев')) {
      return responses.recommend;
    } else if (msg.includes('track') || msg.includes('kuzat') || msg.includes('отслед')) {
      return responses.track;
    } else if (msg.includes('price') || msg.includes('narx') || msg.includes('цен')) {
      return responses.pricing;
    } else if (msg.includes('tip') || msg.includes('maslahat') || msg.includes('совет')) {
      return responses.tips;
    } else if (msg.includes('hello') || msg.includes('salom') || msg.includes('привет') || msg.includes('hi')) {
      return responses.welcome;
    }

    return responses.default;
  };

  // Handle send message
  const handleSendMessage = (messageText = null, action = null) => {
    const text = messageText || inputMessage.trim();
    if (!text && !action) return;
    // Add user message locally
    const userMsg = {
      id: Date.now(),
      text: text,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsTyping(true);

    // If action-based canned response, keep local behavior
    if (action) {
      setTimeout(() => {
        const botResponse = getBotResponse(text, action);
        const botMsg = {
          id: Date.now() + 1,
          text: botResponse,
          sender: 'bot',
          timestamp: new Date()
        };

        setMessages(prev => [...prev, botMsg]);
        setIsTyping(false);
      }, 1000);
      return;
    }

    // Otherwise send to backend and wait for admin reply via polling
    api.post('bot/send/', { text })
      .then(() => {
        // backend saved and forwarded to Telegram group; polling will pick up replies
        setIsTyping(false);
      })
      .catch(() => {
        // If backend not available, fallback to local bot
        const botResponse = getBotResponse(text, action);
        const botMsg = {
          id: Date.now() + 1,
          text: botResponse,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMsg]);
        setIsTyping(false);
      });
  };

  // Handle quick action click
  const handleQuickAction = (action) => {
    const actionText = quickActions[language].find(a => a.action === action)?.text || '';
    handleSendMessage(actionText, action);
  };

  return (
    <>
      {/* Chat Button */}
      <div
        className={`chatbot-button ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
        role="button"
        tabIndex={0}
        aria-label="EcoBot"
        onKeyDown={(e) => e.key === 'Enter' && setIsOpen(true)}
      >
        <img className="chat-mascot" src="/images/stickers/mascot.webp" alt="" />
        <div className="chat-badge">
          <i className="fas fa-seedling"></i>
        </div>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-left">
              <div className="bot-avatar">
                <img src="/images/stickers/mascot.webp" alt="" />
              </div>
              <div className="bot-info">
                <h3>EcoBot</h3>
                <span className="bot-status">
                  <span className="status-dot"></span>
                  {language === 'en' ? 'Online' : language === 'ru' ? 'В сети' : 'Onlayn'}
                </span>
              </div>
            </div>
            <button className="chatbot-close" onClick={() => setIsOpen(false)} aria-label="Close">
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.sender}`}>
                {message.sender === 'bot' && (
                  <div className="message-avatar">
                    <img src="/images/stickers/mascot.webp" alt="" />
                  </div>
                )}
                <div className="message-bubble">
                  <div className="message-text">{message.text}</div>
                  <div className="message-time">
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                {message.sender === 'user' && (
                  <div className="message-avatar user">
                    <i className="fas fa-user"></i>
                  </div>
                )}
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="message bot">
                <div className="message-avatar">
                  <img src="/images/stickers/mascot.webp" alt="" />
                </div>
                <div className="message-bubble typing">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="chatbot-quick-actions">
            {quickActions[language].map((action) => (
              <button
                key={action.id}
                className="quick-action-btn"
                onClick={() => handleQuickAction(action.action)}
              >
                {action.text}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="chatbot-input">
            <input
              type="text"
              placeholder={language === 'en' ? 'Type your message...' : language === 'ru' ? 'Введите сообщение...' : 'Xabar yozing...'}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            />
            <button className="send-button" onClick={() => handleSendMessage()}>
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;